import { useEffect, useState } from 'react';
import {
  ACTION_TEXT,
  ACTIONS_FOR,
  MODE_HELP,
  MODE_TEXT,
  liveBlockers,
  policyChanges,
  type EvalSummary,
  type ModAction,
  type ModMode,
  type PolicyFields,
} from '../../lib/moderationView';

const MODES: ModMode[] = ['off', 'shadow', 'live'];

/**
 * Moderation policy editor (bot settings and the platform default). `live` is offered only when the latest
 * evaluation proved every category the draft would hide/delete — the server enforces the same rule; this shows why.
 */
export function PolicyForm({
  value,
  run,
  sourceNote,
  onSave,
  onReset,
}: {
  value: PolicyFields;
  run: EvalSummary | null;
  sourceNote?: string;
  onSave: (fields: PolicyFields) => Promise<void>;
  onReset?: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<PolicyFields>(value);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(value), [value]);

  // Locks are computed from the draft, so changing an action re-checks live before saving.
  const blockers = liveBlockers({ ...draft, mode: 'live' }, run);
  const draftBlockers = liveBlockers(draft, run);
  const changes = policyChanges(value, draft);
  const set = <K extends keyof PolicyFields>(k: K, v: PolicyFields[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await onSave(draft);
      setConfirming(false);
    } catch {
      /* the page shows the error */
    } finally {
      setBusy(false);
    }
  };

  const actionSelect = (k: 'abuseAction' | 'spamAction' | 'complaintAction', options: ModAction[]) => (
    <select
      value={draft[k]}
      onChange={(e) => set(k, e.target.value as ModAction)}
      className="justify-self-start rounded border border-gray-300 px-2 py-1 text-sm"
    >
      {options.map((a) => (
        <option key={a} value={a}>
          {ACTION_TEXT[a]}
        </option>
      ))}
    </select>
  );

  return (
    <div className="space-y-4 rounded border border-gray-200 bg-white p-4">
      {sourceNote && <p className="text-xs text-gray-500">{sourceNote}</p>}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Mode</legend>
        {MODES.map((m) => {
          const disabled = m === 'live' && blockers.length > 0 && value.mode !== 'live';
          return (
            <label key={m} className={`flex items-start gap-2 text-sm ${disabled ? 'opacity-50' : ''}`}>
              <input type="radio" name="mode" checked={draft.mode === m} disabled={disabled} onChange={() => set('mode', m)} />
              <span>
                <span className="font-medium">{MODE_TEXT[m]}</span> — <span className="text-gray-600">{MODE_HELP[m]}</span>
              </span>
            </label>
          );
        })}
        {blockers.length > 0 && (
          <div className="rounded bg-amber-50 p-2 text-xs text-amber-800">
            Live is locked until detection is proven:
            <ul className="ml-4 list-disc">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        )}
      </fieldset>
      <div className="grid grid-cols-[8rem_1fr] items-center gap-2 text-sm">
        <span>Abuse</span>
        {actionSelect('abuseAction', ACTIONS_FOR.abuse)}
        <span>Spam</span>
        {actionSelect('spamAction', ACTIONS_FOR.spam)}
        <span>Complaint</span>
        {actionSelect('complaintAction', ACTIONS_FOR.complaint)}
        <span>Notify operator</span>
        <input
          type="checkbox"
          checked={draft.notifyOperator}
          onChange={(e) => set('notifyOperator', e.target.checked)}
          className="justify-self-start"
        />
      </div>
      <p className="text-xs text-gray-500">
        Complaints are never hidden or deleted — an unhappy customer should be answered, not silenced.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!changes.length || draftBlockers.length > 0 || busy}
          onClick={() => setConfirming(true)}
          className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          Save
        </button>
        {onReset && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onReset()}
            className="rounded border border-gray-300 px-3 py-1 text-sm"
          >
            Use platform default
          </button>
        )}
      </div>
      {confirming && (
        <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm">
          <p className="font-medium">Save these changes?</p>
          <ul className="ml-4 list-disc">
            {changes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          {draft.mode === 'live' && <p className="mt-1 text-amber-800">Live mode acts on real customers' comments.</p>}
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={busy} onClick={save} className="rounded bg-blue-600 px-3 py-1 text-white">
              Confirm
            </button>
            <button type="button" onClick={() => setConfirming(false)} className="rounded border border-gray-300 px-3 py-1">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
